import { Controller } from '@hotwired/stimulus';

// Monthly-payment calculator for /prices/:make/:model/:year and the public
// VDP. Pure client-side arithmetic — no network calls, no libraries, nothing
// that could cost those pages their PSI budget or their edge-cacheability.
//
// The server renders a complete default result (price × 10% down × 7% APR ×
// 60 months), so the block reads correctly with JS disabled; this controller
// only recomputes as the visitor moves the controls.
export default class extends Controller {
  static targets = [
    'price', 'down', 'downPct', 'apr', 'termButton', 'term',
    'monthly', 'financed', 'interest', 'total', 'summary', 'priceDescription',
  ];

  static values = { price: Number, term: Number, apr: Number, downPct: Number };

  connect() {
    this.price = this.hasPriceValue && this.priceValue > 0 ? this.priceValue : 25000;
    this.term = this.hasTermValue && this.termValue > 0 ? this.termValue : 60;
    this.apr = this.hasAprValue ? this.aprValue : 7.0;
    this.down = Math.round((this.price * (this.hasDownPctValue ? this.downPctValue : 10)) / 100);
    this.fmt = new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    });
    // The two dollar INPUTS are type=text so they can carry separators
    // ("18,493"); this formats what goes back into them.
    this.num = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

    // The configurator above re-prices the car as the visitor picks a trim,
    // mileage or state — keep the payment in step with the number on screen.
    this.onEstimate = (event) => {
      const next = Number(event.detail && event.detail.price);
      if (!next) {
        ['monthly', 'financed', 'interest', 'total', 'summary'].forEach(name => {
          if (this[`has${name[0].toUpperCase() + name.slice(1)}Target`]) this[`${name}Target`].textContent = '—';
        });
        if (this.hasPriceDescriptionTarget) this.priceDescriptionTarget.textContent = 'No estimate is available for the selected market. Enter a vehicle price to calculate payments.';
        return;
      }
      const pct = this.price > 0 ? this.down / this.price : 0.1;
      this.price = next;
      this.down = Math.round(next * pct);
      this.formatAmounts();
      this.render();
    };
    window.addEventListener('seo:estimate-changed', this.onEstimate);
    this.render();
    // Ask after subscribing: configurator may have connected first.
    window.dispatchEvent(new CustomEvent('seo:request-estimate'));
  }

  disconnect() {
    window.removeEventListener('seo:estimate-changed', this.onEstimate);
  }

  // Amount fields hold "18,493". Strip anything that isn't part of the number
  // so the arithmetic never sees a separator (Number("18,493") is NaN).
  parseAmount(raw) {
    return Math.max(0, Number(String(raw).replace(/[^0-9.]/g, '')) || 0);
  }

  // Re-punctuate on blur, never while typing: rewriting the value mid-entry
  // moves the caret and makes the field impossible to edit from the middle.
  formatAmounts() {
    if (this.hasPriceTarget) this.priceTarget.value = this.num.format(Math.round(this.price));
    if (this.hasDownTarget) this.downTarget.value = this.num.format(Math.round(this.down));
  }

  changePrice(event) {
    this.price = this.parseAmount(event.currentTarget.value);
    this.render();
  }

  changeDown(event) {
    this.down = this.parseAmount(event.currentTarget.value);
    this.render();
  }

  changeApr(event) {
    this.apr = Math.max(0, Number(event.currentTarget.value) || 0);
    this.render();
  }

  selectTerm(event) {
    this.term = Number(event.currentTarget.dataset.term) || 60;
    this.render();
  }

  // Standard annuity payment. A 0% APR loan is a plain division — the
  // annuity formula divides by zero there.
  monthlyPayment(principal, annualRatePct, months) {
    if (months <= 0) return 0;
    if (principal <= 0) return 0;
    const r = annualRatePct / 100 / 12;
    if (r === 0) return principal / months;
    const factor = Math.pow(1 + r, months);
    return (principal * r * factor) / (factor - 1);
  }

  render() {
    // Never finance more than the car costs.
    const down = Math.min(this.down, this.price);
    const financed = Math.max(0, this.price - down);
    const monthly = this.monthlyPayment(financed, this.apr, this.term);
    const total = monthly * this.term;

    if (this.hasPriceDescriptionTarget) this.priceDescriptionTarget.textContent = this.fmt.format(this.price);
    this.element.ownerDocument.querySelectorAll('[data-jump="monthly"]').forEach((link) => {
      link.textContent = `≈ ${this.fmt.format(Math.round(monthly))}/mo loan ↓`;
    });

    this.termButtonTargets.forEach((btn) => {
      const on = Number(btn.dataset.term) === this.term;
      btn.classList.toggle('pay-calc__seg--active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    if (this.hasDownPctTarget) {
      const pct = this.price > 0 ? Math.round((down * 100) / this.price) : 0;
      this.downPctTarget.textContent = `${pct}% down`;
    }
    if (this.hasMonthlyTarget) this.monthlyTarget.textContent = this.fmt.format(Math.round(monthly));
    if (this.hasFinancedTarget) this.financedTarget.textContent = this.fmt.format(Math.round(financed));
    if (this.hasInterestTarget) {
      this.interestTarget.textContent = this.fmt.format(Math.max(0, Math.round(total - financed)));
    }
    if (this.hasTotalTarget) this.totalTarget.textContent = this.fmt.format(Math.round(total));
    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent =
        `${this.term} months · ${this.apr}% APR · ${this.fmt.format(Math.round(down))} down`;
    }
  }
}
